import { recordDelayCost, recordNpr, statusLabel } from "./calc";
import type { ProblemRecord, Program, WorkflowStage } from "./types";

/** Read-only projection: record closure and stage completion remain authoritative. */
export function openRecordWork(records: ProblemRecord[]) {
  return records
    .filter((record) => record.status !== "closed")
    .map((record) => ({
      record,
      stages: [...record.stages]
        .filter((stage) => stage.status !== "complete")
        .sort((a, b) => a.index - b.index),
    }))
    .sort((a, b) => a.record.code.localeCompare(b.record.code));
}

export function dateIsPast(date: string, today: string) {
  const day = date.slice(0, 10);
  return /^\d{4}-\d{2}-\d{2}$/.test(day) && day < today.slice(0, 10);
}

export interface OpenStageRow {
  record: ProblemRecord;
  stage: WorkflowStage;
  overdue: boolean;
}

export interface ProgramBacklog {
  programId: string;
  code: string;
  name: string;
  modelYear: number;
  open: number;
  outstanding: number;
  overdue: number;
  critical: number;
  exposure: number;
  remaining: number;
  delay: number;
}

export interface Backlog {
  key: string;
  label: string;
  stages: number;
  overdue: number;
  records: number;
}

export interface OpenSummary {
  records: number;
  outstanding: number;
  inProgress: number;
  notStarted: number;
  overdue: number;
  recordsWithOverdue: number;
  critical: number;
  pastTarget: number;
  exposure: number;
  actual: number;
  remaining: number;
  delay: number;
  oldestOpenedAt: string | null;
  byProgram: ProgramBacklog[];
  byStage: Backlog[];
  byOwner: Backlog[];
  byStatus: Backlog[];
}

const STATUS_ORDER = ["signal", "containment", "analysis", "implementation", "verification"];

/**
 * Read-only combined summary of open Problem Records. Nothing is written; stage
 * and record state remain the authority. Overdue counts are withheld (0) until a
 * real "today" is supplied, so no date judgement is made server-side.
 */
export function openRecordSummary(
  records: ProblemRecord[],
  programs: Program[],
  today: string,
): OpenSummary {
  const work = openRecordWork(records);
  const past = (date: string) => (today ? dateIsPast(date, today) : false);
  const rows: OpenStageRow[] = work.flatMap(({ record, stages }) =>
    stages.map((stage) => ({ record, stage, overdue: past(stage.dueDate) })),
  );

  const group = (
    keyOf: (row: OpenStageRow) => string,
    labelOf: (key: string) => string,
    order?: string[],
  ): Backlog[] => {
    const map = new Map<string, { stages: number; overdue: number; records: Set<string> }>();
    for (const row of rows) {
      const key = keyOf(row);
      const entry = map.get(key) ?? { stages: 0, overdue: 0, records: new Set<string>() };
      entry.stages += 1;
      if (row.overdue) entry.overdue += 1;
      entry.records.add(row.record.id);
      map.set(key, entry);
    }
    const items = [...map].map(([key, e]) => ({
      key,
      label: labelOf(key),
      stages: e.stages,
      overdue: e.overdue,
      records: e.records.size,
    }));
    if (order) {
      items.sort((a, b) => {
        const ai = order.indexOf(a.key);
        const bi = order.indexOf(b.key);
        return (ai === -1 ? order.length : ai) - (bi === -1 ? order.length : bi);
      });
    } else {
      items.sort(
        (a, b) => b.overdue - a.overdue || b.stages - a.stages || a.label.localeCompare(b.label),
      );
    }
    return items;
  };

  const byProgram: ProgramBacklog[] = programs
    .map((program) => {
      const scoped = work.filter(({ record }) => record.programId === program.id);
      const stageRows = rows.filter(({ record }) => record.programId === program.id);
      return {
        programId: program.id,
        code: program.code,
        name: program.name,
        modelYear: program.modelYear,
        open: scoped.length,
        outstanding: stageRows.length,
        overdue: stageRows.filter((r) => r.overdue).length,
        critical: scoped.filter(({ record }) => record.risk === "critical").length,
        exposure: scoped.reduce((a, { record }) => a + record.financial.exposure, 0),
        remaining: scoped.reduce((a, { record }) => a + recordNpr(record), 0),
        delay: scoped.reduce((a, { record }) => a + recordDelayCost(record), 0),
      };
    })
    .filter((p) => p.open > 0)
    .sort(
      (a, b) => b.overdue - a.overdue || b.remaining - a.remaining || a.code.localeCompare(b.code),
    );

  return {
    records: work.length,
    outstanding: rows.length,
    inProgress: rows.filter((r) => r.stage.status === "in-progress").length,
    notStarted: rows.filter((r) => r.stage.status === "not-started").length,
    overdue: rows.filter((r) => r.overdue).length,
    recordsWithOverdue: new Set(rows.filter((r) => r.overdue).map((r) => r.record.id)).size,
    critical: work.filter(({ record }) => record.risk === "critical").length,
    pastTarget: work.filter(({ record }) => past(record.targetCloseAt)).length,
    exposure: work.reduce((a, { record }) => a + record.financial.exposure, 0),
    actual: work.reduce((a, { record }) => a + record.financial.actualCost, 0),
    remaining: work.reduce((a, { record }) => a + recordNpr(record), 0),
    delay: work.reduce((a, { record }) => a + recordDelayCost(record), 0),
    oldestOpenedAt: work.length
      ? work.reduce(
          (a, { record }) => (record.openedAt < a ? record.openedAt : a),
          work[0]!.record.openedAt,
        )
      : null,
    byProgram,
    byStage: group(
      (row) => String(row.stage.index),
      (key) => {
        const found = rows.find((r) => String(r.stage.index) === key);
        return `Stage ${key} · ${found?.stage.name ?? ""}`;
      },
      STATUS_ORDER.map(String).concat(
        [...new Set(rows.map((r) => String(r.stage.index)))].sort((a, b) => Number(a) - Number(b)),
      ),
    ),
    byOwner: group(
      (row) => row.stage.owner || "",
      (key) => key || "Unassigned",
    ),
    byStatus: group(
      (row) => row.record.status,
      (key) => statusLabel[key] ?? key,
      STATUS_ORDER,
    ),
  };
}
